import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom, BehaviorSubject, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Config } from './config.model';

import { logger } from '../../helpers/logger';
/**
 * Service responsible for loading runtime configuration from config.json.
 * This service is used during app initialization to load configuration
 * that can be overridden at deployment time.
 */
@Injectable({ providedIn: 'root' })
export class ConfigService {
  private readonly http = inject(HttpClient);
  private runtimeConfig: Partial<Config> = {};
  private loadedSubject = new BehaviorSubject<boolean>(false);
  public loaded = false;

  /**
   * Loads configuration from config.json file.
   * This is called during app initialization.
   */
  public async loadConfig(): Promise<void> {
    try {
      this.runtimeConfig = await lastValueFrom(this.http.get<Partial<Config>>('/config/config.json'));
    } catch (error) {
      logger.warn('Failed to load runtime configuration', error);
      this.runtimeConfig = {};
    }
    this.loaded = true;
    this.loadedSubject.next(true);
  }

  /**
   * Returns the runtime configuration loaded from config.json.
   */
  public getRuntimeConfig(): Partial<Config> {
    return this.runtimeConfig;
  }

  /**
   * Returns the runtime configuration, waiting for it to load if necessary.
   * This method blocks until the config is available.
   */
  public async getRuntimeConfigAsync(): Promise<Partial<Config>> {
    if (this.loaded) {
      return this.runtimeConfig;
    }

    // Wait for config to be loaded
    await firstValueFrom(this.loadedSubject.pipe(filter(loaded => loaded)));
    return this.runtimeConfig;
  }

  /**
   * Synchronously returns the runtime config if loaded, otherwise returns empty config.
   * This is safe to call during provider setup but may return incomplete data.
   */
  public getRuntimeConfigSync(): Partial<Config> {
    if (this.loaded) {
      return this.runtimeConfig;
    }
    console.warn('Config not yet loaded, returning empty config');
    return {};
  }
}
